// /:partnerId/change-password?client_id=…&id_token_hint=…[&return_url=…]
//
// Autogestión: el usuario YA logueado en la app del partner cambia su
// contraseña sabiendo la actual. Distinto del reset por mail
// (/forgot-password → /reset-password), que prueba posesión del inbox.
//
// Identidad: la OTT manda `id_token_hint` (el id_token que ya tiene) y lo
// canjeamos por una sesión corta vía POST /me/session. Si la app prefiere
// pasar directamente un access_token nuestro, acepta `?access_token=` — no lo
// mintea nadie de este lado, solo se usa como Bearer.
//
// Estados:
//   - loading: canjeando el id_token_hint
//   - invalid: sin identidad usable (link mal armado o token vencido)
//   - editing / submitting: form abierto
//   - done: contraseña cambiada (+ botón volver a la app si return_url_ok)

import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthShell from '@/components/AuthShell';
import FormField from '@/components/FormField';
import SubmitButton from '@/components/SubmitButton';
import PasswordChecklist, { PasswordMatchHint } from '@/components/PasswordChecklist';
import { PASSWORD_MIN_LENGTH, isPasswordValid } from '@/lib/password-policy';
import { scrollToField } from '@/lib/dom';
import { FlowExitLink } from '@/components/FlowNav';
import {
  initSelfCareSession,
  fetchMeAccount,
  changeMyPassword,
  ApiError,
} from '@/lib/api';

type Boot =
  | { kind: 'loading' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'ready'; token: string; email: string | null; returnUrlOk: boolean };

export default function ChangePassword(): JSX.Element {
  // Sin partnerId: los endpoints /me/* lo derivan de la sesión (Bearer), no
  // del path. La ruta vive igual bajo /:partnerId/ por el branding.
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const idTokenHint = searchParams.get('id_token_hint');
  const presetToken = searchParams.get('access_token');
  const returnUrl = searchParams.get('return_url');
  // Si la app mandó `state`, se lo devolvemos al salir para que pueda correlacionar.
  const oauthState = searchParams.get('state');

  const [boot, setBoot] = useState<Boot>({ kind: 'loading' });
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [revokeOthers, setRevokeOthers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<{ otherSessionsRevoked: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<'current' | 'password' | 'confirm' | null>(null);
  const currentRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  // Boot: resuelve la sesión una sola vez. Las deps son los query params
  // (estables mientras no cambie la URL) — sin objetos derivados en deps para
  // no re-disparar el efecto en cada render.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        let token: string;
        let returnUrlOk = false;
        if (presetToken) {
          token = presetToken;
        } else if (clientId && idTokenHint) {
          const session = await initSelfCareSession({
            clientId,
            idTokenHint,
            ...(returnUrl ? { returnUrl } : {}),
          });
          token = session.access_token;
          returnUrlOk = session.return_url_ok;
        } else {
          if (!cancelled) {
            setBoot({
              kind: 'invalid',
              reason: 'El link no trae los datos de tu sesión. Volvé a abrirlo desde la app.',
            });
          }
          return;
        }
        // El email es solo informativo (confirma de qué cuenta hablamos). Si
        // falla, seguimos igual: el cambio no depende de mostrarlo.
        const email = await fetchMeAccount(token)
          .then((acct) => acct.email)
          .catch(() => null);
        if (!cancelled) setBoot({ kind: 'ready', token, email, returnUrlOk });
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof ApiError && e.code === 'invalid_request_body'
            ? 'El link venció o no es válido. Volvé a abrirlo desde la app.'
            : 'No pudimos validar tu sesión. Probá de nuevo en un momento.';
        setBoot({ kind: 'invalid', reason: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, idTokenHint, presetToken, returnUrl]);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (boot.kind !== 'ready') return;
    setErr(null);

    if (current.length === 0) {
      setErr('Ingresá tu contraseña actual.');
      setInvalidField('current');
      scrollToField(currentRef.current);
      return;
    }
    if (!isPasswordValid(password)) {
      setErr('La contraseña nueva todavía no cumple todos los requisitos. Revisá la lista debajo del campo.');
      setInvalidField('password');
      scrollToField(passwordRef.current);
      return;
    }
    if (password === current) {
      setErr('La contraseña nueva tiene que ser distinta de la actual.');
      setInvalidField('password');
      scrollToField(passwordRef.current);
      return;
    }
    if (password !== confirm) {
      setErr('Las contraseñas no coinciden.');
      setInvalidField('confirm');
      scrollToField(confirmRef.current);
      return;
    }

    setLoading(true);
    try {
      const res = await changeMyPassword(boot.token, {
        currentPassword: current,
        newPassword: password,
        revokeOtherSessions: revokeOthers,
      });
      setDone({ otherSessionsRevoked: res.other_sessions_revoked });
    } catch (e) {
      if (e instanceof ApiError && e.message === 'current_password_incorrect') {
        setErr('La contraseña actual no es correcta.');
        setInvalidField('current');
        scrollToField(currentRef.current);
      } else if (e instanceof ApiError && e.code === 'rate_limited') {
        setErr('Demasiados intentos. Esperá unos minutos antes de volver a probar.');
      } else {
        setErr(
          e instanceof ApiError
            ? e.message
            : 'No pudimos cambiar la contraseña. Probá de nuevo en un momento.',
        );
      }
    } finally {
      setLoading(false);
    }
  }

  if (boot.kind === 'loading') {
    return (
      <AuthShell title="Cambiar contraseña">
        <div role="status" aria-live="polite" className="text-sm text-text-muted">
          Validando tu sesión…
        </div>
      </AuthShell>
    );
  }

  if (boot.kind === 'invalid') {
    return (
      <AuthShell title="Link inválido" subtitle="No pudimos identificar tu cuenta.">
        <div
          role="alert"
          className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
        >
          {boot.reason}
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Contraseña actualizada">
        <div className="space-y-3">
          <div
            role="status"
            className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text space-y-2"
          >
            <p>Listo. Tu contraseña fue actualizada con éxito.</p>
            <p className="text-text-muted text-xs">
              {done.otherSessionsRevoked > 0
                ? `Cerramos ${done.otherSessionsRevoked} ${done.otherSessionsRevoked === 1 ? 'sesión' : 'sesiones'} en tus otros dispositivos. Vas a tener que ingresar de nuevo con la contraseña nueva.`
                : 'Tus otras sesiones siguen abiertas. Podés cerrarlas cuando quieras desde la app.'}
            </p>
          </div>
          {boot.returnUrlOk && returnUrl ? (
            <a
              href={returnUrl}
              className="block w-full rounded-lg border border-accent px-4 py-2.5 text-center text-sm font-medium text-accent hover:bg-accent/10"
            >
              Volver a la app
            </a>
          ) : null}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Cambiar contraseña"
      subtitle={
        boot.email
          ? `Cuenta: ${boot.email}`
          : 'Ingresá tu contraseña actual y elegí una nueva.'
      }
      // Solo si el return_url pasó la whitelist del client: sin destino válido
      // no ofrecemos la salida.
      flowExit={
        boot.returnUrlOk && returnUrl ? (
          <FlowExitLink exit={{ kind: 'return_url', returnUrl, state: oauthState }} />
        ) : null
      }
    >
      <form onSubmit={onSubmit} aria-busy={loading ? 'true' : undefined} className="space-y-4" noValidate>
        <FormField
          ref={currentRef}
          name="current-password"
          type="password"
          label="Contraseña actual"
          autoComplete="current-password"
          required
          autoFocus
          invalid={invalidField === 'current'}
          value={current}
          onChange={(e) => {
            setCurrent(e.target.value);
            if (invalidField === 'current') setInvalidField(null);
          }}
        />
        <FormField
          ref={passwordRef}
          name="new-password"
          type="password"
          label="Contraseña nueva"
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          required
          invalid={invalidField === 'password'}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (invalidField === 'password') setInvalidField(null);
          }}
        />
        <PasswordChecklist password={password} />
        <FormField
          ref={confirmRef}
          name="confirm-password"
          type="password"
          label="Repetí la contraseña nueva"
          autoComplete="new-password"
          minLength={PASSWORD_MIN_LENGTH}
          required
          invalid={invalidField === 'confirm'}
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            if (invalidField === 'confirm') setInvalidField(null);
          }}
        />
        <PasswordMatchHint password={password} confirm={confirm} />

        {/* El usuario decide si echa a sus otros dispositivos. La sesión actual
            nunca se cierra: si no, se autoexpulsaría del propio formulario. */}
        <label className="flex items-start gap-3 rounded-lg border border-text-muted/20 p-3 text-sm text-text">
          <input
            type="checkbox"
            name="revoke-other-sessions"
            className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]"
            checked={revokeOthers}
            onChange={(e) => setRevokeOthers(e.target.checked)}
          />
          <span>
            Cerrar sesión en mis otros dispositivos
            <span className="block text-xs text-text-muted">
              Smart TV, celular y otros navegadores van a tener que ingresar de nuevo. Tu sesión acá
              sigue abierta.
            </span>
          </span>
        </label>

        {err ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
          >
            {err}
          </div>
        ) : null}
        <SubmitButton loading={loading} loadingLabel="Cambiando…">
          Cambiar contraseña
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
